import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input, Card, CardContent, Button } from '../components/ui';
import { GameRegistry, RegisteredGame } from '../components/game';
import { loadGames } from '../services/game.service';

const categories = [
  { id: 'all', name: 'All Games', icon: '🎮' },
  { id: 'retro', name: 'Retro', icon: '🕹️' },
  { id: 'arcade', name: 'Arcade', icon: '👾' },
  { id: 'puzzle', name: 'Puzzle', icon: '🧩' },
  { id: 'casual', name: 'Casual', icon: '🌟' },
  { id: 'action', name: 'Action', icon: '⚡' },
];

// Game emoji icons
const gameIcons: Record<string, string> = {
  tennis: '🏓',
  snake: '🐍',
  'wall-breaker': '🧱',
  'falling-blocks': '🧩',
  invaders: '👾',
  'pill-muncher': '👻',
  blasteroids: '🚀',
  'frog-jump': '🐸',
  'flapping-chick': '🐦',
  'alien-attack': '🛸',
  'missile-defense': '🎯',
  solitaire: '🃏',
  'plant-defense': '🌻',
  'tower-defense': '🏰',
  'crypt-runner': '💀',
  'dungeon-crawl': '⚔️',
  onslaught: '💥',
  'lab-attack': '🚀',
  'vexed-vultures': '🐦',
};

type SortOption = 'name' | 'category' | 'difficulty';

export function GameCatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [games, setGames] = useState<RegisteredGame[]>([]);
  const [loading, setLoading] = useState(true);

  const activeCategory = searchParams.get('category') || 'all';

  // Load games on mount
  useEffect(() => {
    loadGames().then(() => {
      setGames(GameRegistry.getAll());
      setLoading(false);
    });
  }, []);

  const filteredGames = useMemo(() => {
    let result = [...games];

    // Filter by category
    if (activeCategory !== 'all') {
      result = result.filter((game) => game.config.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (game) =>
          game.config.title.toLowerCase().includes(query) ||
          game.config.description.toLowerCase().includes(query)
      );
    }

    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.config.title.localeCompare(b.config.title));
        break;
      case 'category':
        result.sort((a, b) =>
          a.config.category.localeCompare(b.config.category)
        );
        break;
      case 'difficulty':
        {
          const diffOrder = { easy: 0, medium: 1, hard: 2 };
          result.sort(
            (a, b) =>
              diffOrder[a.config.difficulty] - diffOrder[b.config.difficulty]
          );
        }
        break;
    }

    return result;
  }, [games, activeCategory, searchQuery, sortBy]);

  const handleCategoryChange = (categoryId: string) => {
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  // Keyboard navigation for category tabs
  const handleCategoryKeyDown = (
    e: React.KeyboardEvent,
    categoryId: string
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCategoryChange(categoryId);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div
          className="flex items-center justify-center min-h-[400px]"
          role="status"
          aria-label="Loading games"
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-slate-400">Loading games...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Game Catalog</h1>
        <p className="text-slate-400">Browse our collection of classic games</p>
      </header>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8" role="search">
        {/* Search */}
        <div className="flex-1">
          <label htmlFor="game-search" className="sr-only">
            Search games
          </label>
          <Input
            id="game-search"
            placeholder="Search games..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            aria-describedby="search-results-count"
          />
        </div>

        {/* Sort */}
        <div>
          <label htmlFor="sort-select" className="sr-only">
            Sort games by
          </label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            aria-label="Sort games"
          >
            <option value="name">Alphabetical</option>
            <option value="category">By Category</option>
            <option value="difficulty">By Difficulty</option>
          </select>
        </div>
      </div>

      {/* Category Tabs */}
      <nav aria-label="Game categories" className="mb-8">
        <ul className="flex flex-wrap gap-2" role="tablist">
          {categories.map((category) => (
            <li key={category.id} role="presentation">
              <Button
                role="tab"
                aria-selected={activeCategory === category.id}
                aria-controls="games-grid"
                variant={activeCategory === category.id ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => handleCategoryChange(category.id)}
                onKeyDown={(e) => handleCategoryKeyDown(e, category.id)}
                tabIndex={0}
              >
                <span aria-hidden="true">{category.icon}</span> {category.name}
              </Button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Results count */}
      <p
        id="search-results-count"
        className="text-slate-400 mb-6"
        aria-live="polite"
      >
        {filteredGames.length} {filteredGames.length === 1 ? 'game' : 'games'}{' '}
        found
      </p>

      {/* Game Grid */}
      <main id="games-grid" role="tabpanel" aria-label="Games list">
        {filteredGames.length > 0 ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGames.map((game) => (
              <li key={game.config.id}>
                <Link
                  to={`/games/${game.config.id}`}
                  aria-label={`Play ${game.config.title} - ${game.config.description}`}
                  className="block h-full focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-lg"
                >
                  <Card
                    variant="interactive"
                    padding="none"
                    className="group overflow-hidden h-full"
                  >
                    <div
                      className="aspect-video bg-slate-700/50 flex items-center justify-center text-6xl"
                      role="img"
                      aria-label={`${game.config.title} game icon`}
                    >
                      {gameIcons[game.config.id] || '🎮'}
                    </div>
                    <CardContent className="p-4">
                      <h2 className="text-lg font-semibold text-white group-hover:text-purple-400 transition-colors">
                        {game.config.title}
                      </h2>
                      <p className="text-sm text-slate-400 mt-1">
                        {game.config.description}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-slate-500 capitalize">
                          {game.config.category}
                        </span>
                        <span className="text-xs text-slate-500 capitalize">
                          {game.config.difficulty}
                        </span>
                      </div>
                      {game.config.tier === 'premium' && (
                        <span
                          className="inline-block mt-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded"
                          aria-label="Premium game"
                        >
                          ⭐ Premium
                        </span>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <Card className="text-center py-12" role="status">
            <p className="text-slate-400 text-lg">
              No games found matching your criteria.
            </p>
            <Button
              variant="ghost"
              className="mt-4"
              onClick={() => {
                setSearchQuery('');
                handleCategoryChange('all');
              }}
              aria-label="Clear all filters"
            >
              Clear filters
            </Button>
          </Card>
        )}
      </main>

      {/* Keyboard navigation hint */}
      <footer
        className="mt-8 text-center text-slate-500 text-sm"
        aria-label="Accessibility information"
      >
        <p>Use Tab to navigate between games. Press Enter to play.</p>
      </footer>
    </div>
  );
}
